/**
 * With build-time config the app is the product: sign in, terms, subjects, upload.
 * Without it (check-web builds, the design demos) the three fixture surfaces remain at
 * #paper / #study / #review so the design work stays reviewable.
 */
import { useEffect, useState } from "react";
import { AuthProvider } from "./app/AuthProvider";
import { useRoute } from "./app/router";
import { Shell } from "./app/Shell";
import { StudyView } from "./cards/StudyView";
import { appConfig } from "./config";
import { sampleDeck } from "./fixtures/sampleDeck";
import { sampleDocument } from "./fixtures/sampleDocument";
import { samplePaper } from "./fixtures/samplePaper";
import { PaperView } from "./paper/PaperView";
import { ChapterReview } from "./review/ChapterReview";
import { PUBLIC_ROUTES, Site } from "./site/Site";
import "./styles/tokens.css";
import "./styles/components.css";
import "./styles/app.css";

type Surface = "paper" | "study" | "review";

function surfaceFromHash(): Surface {
  if (window.location.hash === "#study") return "study";
  if (window.location.hash === "#review") return "review";
  return "paper";
}

function Demos() {
  const [surface, setSurface] = useState<Surface>(surfaceFromHash);
  useEffect(() => {
    const on = () => setSurface(surfaceFromHash());
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  if (surface === "study") return <StudyView deck={sampleDeck} />;
  if (surface === "review") return <ChapterReview document={sampleDocument} />;
  return <PaperView paper={samplePaper} board="Computer Science · Paper 1 style" />;
}

export function App() {
  const route = useRoute();
  if (!appConfig || window.location.hash.startsWith("#")) return <Demos />;
  // The public tier renders without AuthProvider, so a stranger is never redirected to
  // the hosted login just for reading the landing or the terms (spec §6.1).
  if ((PUBLIC_ROUTES as readonly string[]).includes(route))
    return <Site cfg={appConfig} route={route} />;
  return (
    <AuthProvider cfg={appConfig}>
      <Shell cfg={appConfig} />
    </AuthProvider>
  );
}
